// guest_home.js — Guest Homepage: Featured Events Carousel + Hero Search
// Loaded as type="module" → window.__ is available (deferred execution)

import { fetchJson } from './http/utils.js';
import { icon } from './shared/icons/render.js';

const __ = window.__ || (s => s);

const API_BASE = '/api/method/';

// ---------- Security: Escape für HTML-Attribute (XSS-Prävention) ----------
function escapeAttr(str) {
  if (str == null) return '';
  return String(str).replace(/['"<>&]/g, c => ({
    "'": '&#39;', '"': '&quot;', '<': '&lt;', '>': '&gt;', '&': '&amp;'
  }[c]));
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

function safeCssUrl(url) {
  if (!url) return '';
  const raw = String(url).trim();
  if (!raw) return '';
  if (!/^(\/(?!\/)|https?:\/\/)/i.test(raw)) return '';
  if (/[\u0000-\u001F\u007F]/.test(raw)) return '';
  return encodeURI(raw).replace(/['"()\\]/g, (ch) =>
    '%' + ch.charCodeAt(0).toString(16).toUpperCase()
  );
}

// ---------- Mini HTTP GET ----------
async function apiGet(method, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const url = `${API_BASE}${method}${qs ? '?' + qs : ''}`;
  const { res, payload, normalized } = await fetchJson(url, { method: 'GET', cache: 'no-store' });
  return { res, payload, normalized };
}

// ---------- Date Formatting ----------
function formatDateBadge(dateStr) {
  if (!dateStr) return { day: '', month: '' };
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { day: '', month: '' };
    return {
      day: d.getDate().toString(),
      month: d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '')
    };
  } catch { return { day: '', month: '' }; }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
}

// ---------- Featured Events Card HTML (discover-card Pattern) ----------
function renderFeaturedCard(event) {
  const imageUrl = safeCssUrl(event.image || event.banner_image || '/assets/cita_events/images/event/cita_default_messe.png');
  const slug = escapeAttr(event.slug || '');
  const title = escapeHtml(event.title || __('Event'));
  const date = formatDateBadge(event.start_date);
  const location = escapeHtml(event.location || event.city || '');
  const eventType = escapeHtml(event.event_type || event.category || '');
  const startDate = formatDate(event.start_date);
  const endDate = event.end_date ? formatDate(event.end_date) : '';
  const highlightClass = event.is_highlighted ? 'ring-2 ring-cyan-500/50' : '';

  const stretchedLink = event.slug
    ? `<a class="discover-card-link" href="/event/${slug}" aria-label="${escapeAttr(event.title || __('Event'))} – ${escapeAttr(__('Details ansehen'))}">${title}</a>`
    : '';
  return `
    <article class="discover-card ${highlightClass}">
      ${stretchedLink}
      <div class="discover-header">
        <img src="${escapeAttr(imageUrl)}" alt="${title}" loading="lazy" />
        <div class="discover-header-gradient"></div>
        ${date.day ? `
        <div class="discover-date">
          <div class="discover-date-day">${date.day}</div>
          <div class="discover-date-month">${date.month}</div>
        </div>` : ''}
      </div>
      <div class="discover-content">
        ${eventType ? `<span class="discover-type">${eventType}</span>` : ''}
        <h3 class="discover-title">${title}</h3>
        <div class="discover-meta">
          ${location ? `
          <div class="discover-meta-item">
            <svg fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>${location}</span>
          </div>` : ''}
          ${startDate ? `
          <div class="discover-meta-item">
            <svg fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>
            <span>${startDate}${endDate ? ' – ' + endDate : ''}</span>
          </div>` : ''}
        </div>
        <div class="discover-actions">
          <a href="/event/${slug}" class="discover-btn discover-btn-details">
            ${icon('info')}
            Details
          </a>
          ${event.tickets_available ? `
          <a href="/event/${slug}#tickets" class="discover-btn discover-btn-tickets">
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/></svg>
            Tickets
          </a>` : ''}
        </div>
      </div>
    </article>
  `;
}

// ---------- Load and Render Featured Events ----------
async function loadFeaturedEvents() {
  const wrapper = document.getElementById('featured-swiper-wrapper');
  if (!wrapper) return;

  try {
    const { normalized } = await apiGet('cita_events.api.event.event_public_api.get_curated_sections', { limit: 12 });
    wrapper.innerHTML = '';

    const emptyState = document.getElementById('featured-empty');
    const carousel = wrapper.closest('.swiper');

    if (normalized.success) {
      // Highlights als primäre Quelle, Fallback zu trending oder recommended
      const sections = normalized.data || {};
      const events = sections.highlights?.length ? sections.highlights
                   : sections.trending?.length ? sections.trending
                   : sections.recommended || [];

      if (events.length) {
        for (const event of events) {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          slide.innerHTML = renderFeaturedCard(event);
          wrapper.appendChild(slide);
        }

        if (window.Swiper) {
          new window.Swiper('.featured-events-carousel', {
            slidesPerView: 1.2,
            spaceBetween: 12,
            navigation: {
              nextEl: '.featured-events-carousel .swiper-button-next',
              prevEl: '.featured-events-carousel .swiper-button-prev'
            },
            pagination: {
              el: '.featured-events-carousel .swiper-pagination',
              clickable: true,
              renderBullet: function (index, className) {
                return `<span class="discover-dot ${className}"></span>`;
              }
            },
            breakpoints: {
              480: { slidesPerView: 1.5, spaceBetween: 14 },
              640: { slidesPerView: 2.2, spaceBetween: 16 },
              1024: { slidesPerView: 2.8, spaceBetween: 20 },
            },
          });
        }

        if (emptyState) emptyState.classList.add('hidden');
        if (carousel) carousel.classList.remove('hidden');
      } else {
        if (emptyState) emptyState.classList.remove('hidden');
        if (carousel) carousel.classList.add('hidden');
      }
    } else {
      if (emptyState) emptyState.classList.remove('hidden');
      if (carousel) carousel.classList.add('hidden');
    }
  } catch (err) {
    console.warn('Featured events load failed', err);
    wrapper.innerHTML = '';
    const emptyState = document.getElementById('featured-empty');
    if (emptyState) emptyState.classList.remove('hidden');
  }
}

// ---------- Hero Search ----------
function initHeroSearch() {
  const input = document.getElementById('guest-search');
  if (!input) return;

  let debounceTimer = null;

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = input.value.trim();
      if (query) {
        // Navigate to event listing with search query
        window.location.href = `/event?search=${encodeURIComponent(query)}`;
      } else {
        window.location.href = '/event';
      }
    }
  });

  // Optional: live search suggestions could be added here in the future
}

// ---------- Guest Announcements ----------
async function loadGuestAnnouncements() {
  const container = document.getElementById('announcements-container');
  if (!container) return;

  try {
    const { normalized } = await apiGet('cita_events.api.notifications.get_public_announcements', { limit: 5 });
    if (!normalized.success) return;

    const announcements = normalized.data?.announcements || [];
    if (!announcements.length) return;

    const colorMap = {
      blue:   { bg: 'surface-elevated', border: 'border-cyan-500/30',    icon: 'text-cyan-400' },
      green:  { bg: 'surface-elevated', border: 'border-emerald-500/30', icon: 'text-emerald-400' },
      purple: { bg: 'surface-elevated', border: 'border-violet-500/30',  icon: 'text-violet-400' },
      amber:  { bg: 'surface-elevated', border: 'border-amber-500/30',   icon: 'text-amber-400' },
      red:    { bg: 'surface-elevated', border: 'border-rose-500/30',    icon: 'text-rose-400' },
      transparent: { bg: 'bg-transparent', border: 'border-default-theme', icon: 'text-secondary' },
    };

    const iconSvgMap = {
      info:      '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
      warn:      '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
      megaphone: '<path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>',
      sparkles:  '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/><path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z"/>',
      gift:      '<path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
      bell:      '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
      star:      '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>',
      checkmark: '<circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>',
      calendar:  '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
    };

    let html = '';
    for (const a of announcements) {
      const dismissKey = `announcement_dismissed_${a.code}`;
      if (a.is_dismissable && localStorage.getItem(dismissKey)) continue;

      const colors = colorMap[a.accent_color] || colorMap.blue;
      const iconPath = iconSvgMap[a.icon] || iconSvgMap.info;
      const hasContent = a.message && a.message.length > 0;
      const title = escapeAttr(a.title) || __('Ankündigung');
      const messageHtml = a.message || '';
      const typeLabel = a.announcement_type ? escapeAttr(a.announcement_type) : '';
      const code = escapeAttr(a.code);

      html += `
        <div class="announcement-banner rounded-lg border-l-[3px] ${colors.bg} ${colors.border} backdrop-blur-sm overflow-hidden" data-code="${code}">
          <div class="flex items-start gap-3 px-4 py-2.5 ${hasContent ? 'cursor-pointer hover:bg-white/5 transition' : ''}"
               ${hasContent ? `onclick="toggleAnnouncement('${code}')"` : ''}>
            <svg class="w-4 h-4 ${colors.icon} shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">${iconPath}</svg>
            <div class="flex-1 min-w-0">
              <span class="announcement-title font-medium text-body leading-snug">
                ${title}
                ${hasContent ? icon('chevronDown', { size: 14, className: 'announcement-chevron inline-block w-3.5 h-3.5 text-muted-theme ml-1.5 align-text-bottom transition-transform duration-200' }) : ''}
              </span>
            </div>
            ${typeLabel ? `<span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${colors.bg} ${colors.icon} border ${colors.border} shrink-0">${typeLabel}</span>` : ''}
            ${a.action_url ? `<a href="${escapeAttr(a.action_url)}" class="text-xs font-medium ${colors.icon} hover:text-white whitespace-nowrap transition shrink-0" onclick="event.stopPropagation()">${escapeAttr(a.action_label) || __('Mehr')} →</a>` : ''}
            ${a.is_dismissable ? `
              <button class="text-muted-theme hover:text-secondary p-0.5 transition shrink-0" onclick="event.stopPropagation(); dismissAnnouncement('${code}')">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            ` : ''}
          </div>
          ${hasContent ? `
            <div class="announcement-content hidden px-4 pb-3 pt-0">
              <div class="text-sm text-secondary leading-relaxed pl-7 announcement-message [&_.ql-editor]:p-0 [&_p]:m-0 [&_p+p]:mt-1">${messageHtml}</div>
            </div>
          ` : ''}
        </div>`;
    }

    if (html) {
      container.innerHTML = html;
      container.classList.remove('hidden');
    }
  } catch (err) {
    console.warn('Guest announcements load failed', err);
  }
}

// ---------- Announcement Helpers ----------
function dismissAnnouncement(code) {
  if (!code || !/^[a-zA-Z0-9_-]+$/.test(code) || code.length > 100) return;
  localStorage.setItem(`announcement_dismissed_${code}`, '1');
  const banner = document.querySelector(`.announcement-banner[data-code="${code}"]`);
  if (banner) {
    banner.style.transition = 'opacity 0.2s ease, max-height 0.3s ease';
    banner.style.opacity = '0';
    banner.style.maxHeight = '0';
    banner.style.overflow = 'hidden';
    setTimeout(() => banner.remove(), 300);
  }
}

function toggleAnnouncement(code) {
  if (!code || !/^[a-zA-Z0-9_-]+$/.test(code) || code.length > 100) return;
  const banner = document.querySelector(`.announcement-banner[data-code="${code}"]`);
  if (!banner) return;
  const content = banner.querySelector('.announcement-content');
  const chevron = banner.querySelector('.announcement-chevron');
  if (!content) return;
  const isHidden = content.classList.contains('hidden');
  content.classList.toggle('hidden');
  if (chevron) chevron.style.transform = isHidden ? 'rotate(180deg)' : '';
}

// ---------- Expose helpers globally (module-scoped, but inline onclick needs window access) ----------
window.dismissAnnouncement = dismissAnnouncement;
window.toggleAnnouncement = toggleAnnouncement;

// ---------- DOM Ready ----------
document.addEventListener('DOMContentLoaded', () => {
  loadGuestAnnouncements();
  loadFeaturedEvents();
  initHeroSearch();
});
